import { collection, doc, getDoc, getDocs, query, where, orderBy, updateDoc, deleteDoc, onSnapshot, setDoc } from "firebase/firestore";
import { db, auth, functions } from "@/lib/firebase";
import { httpsCallable } from "firebase/functions";
import { User, InsertUser, UserRole } from "@/lib/schema";

// Get user by Firebase UID
export const getUserByUid = async (uid: string): Promise<User | null> => {
  try {
    const userDoc = await getDoc(doc(db, "users", uid));
    if (userDoc.exists()) {
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    return null;
  } catch (error) {
    console.error("Error getting user:", error);
    return null;
  }
};

// Get user by email
export const getUserByEmail = async (email: string): Promise<User | null> => {
  try {
    const q = query(collection(db, "users"), where("email", "==", email));
    const querySnapshot = await getDocs(q);
    
    if (!querySnapshot.empty) {
      const userDoc = querySnapshot.docs[0];
      return { id: userDoc.id, ...userDoc.data() } as User;
    }
    return null;
  } catch (error) {
    console.error("Error getting user by email:", error);
    return null;
  }
};

// Create user with error handling
export const createUserSafe = async (uid: string, userData: InsertUser): Promise<User> => {
  try {
    // Use setDoc to create or overwrite the document with the UID as document ID
    const userRef = doc(db, "users", uid);
    await setDoc(userRef, userData);
    return { id: uid, ...userData } as User;
  } catch (error: any) {
    console.error("Error creating user:", error);
    throw error;
  }
};

// Update user
export const updateUser = async (uid: string, updates: Partial<User>): Promise<void> => {
  try {
    await updateDoc(doc(db, "users", uid), updates);
  } catch (error) {
    console.error("Error updating user:", error);
    throw error;
  }
};

// Get user by ID (alias for getUserByUid for compatibility)
export const getUser = async (uid: string): Promise<User | null> => {
  return getUserByUid(uid);
};

// Get current user
export const getCurrentUser = async (): Promise<User | null> => {
  if (!auth.currentUser) return null;
  return getUserByUid(auth.currentUser.uid);
};

// Helper function to create a user document for existing Firebase users
export const createUserDocumentIfMissing = async (role?: UserRole) => {
  if (!auth.currentUser) return null;

  const existingUser = await getUserByUid(auth.currentUser.uid);
  if (existingUser) return existingUser;

  // Extract user info from Firebase Auth
  const { uid, email, displayName } = auth.currentUser;
  
  // Create a more robust username generation
  const generateUsername = (displayName: string | null, email: string | null, uid: string): string => {
    if (displayName) {
      // Remove spaces and special characters, convert to lowercase
      const cleanDisplayName = displayName.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      if (cleanDisplayName.length > 0) {
        return cleanDisplayName;
      }
    }
    
    if (email) {
      const emailPrefix = email.split('@')[0];
      if (emailPrefix && emailPrefix.length > 0) {
        return emailPrefix.replace(/[^a-zA-Z0-9]/g, '').toLowerCase();
      }
    }
    
    // Final fallback: use first 8 characters of uid
    return `user${uid.substring(0, 8)}`;
  };
  
  // Split display name for first/last name
  const displayNameParts = (displayName || email?.split('@')[0] || 'Unknown User').split(' ');
  const firstName = displayNameParts[0] || 'Unknown';
  const lastName = displayNameParts.length > 1 ? displayNameParts.slice(1).join(' ') : 'User';
  
  const userData: InsertUser = {
    email: email || '',
    username: generateUsername(displayName, email, uid),
    firstName,
    lastName,
    role: role || UserRole.USER, // Use provided role or default to USER
    passwordResetRequired: false, // Since they already authenticated
    onboardingCompleted: false
  };

  try {
    const newUser = await createUserSafe(uid, userData);
    return newUser;
  } catch (error) {
    console.error("Failed to create user document:", error);
    return null;
  }
};

// Get all users with specific roles
export const getAllUsers = async (roles?: UserRole[]): Promise<User[]> => {
  try {
    let q = query(collection(db, "users"), orderBy("firstName"));
    
    if (roles && roles.length > 0) {
      q = query(collection(db, "users"), where("role", "in", roles), orderBy("firstName"));
    }
    
    const querySnapshot = await getDocs(q);
    return querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as User));
  } catch (error) {
    console.error("Error getting users:", error);
    return [];
  }
};

// Subscribe to real-time user updates with specific roles
export const subscribeToUsers = (
  callback: (users: User[]) => void,
  roles?: UserRole[]
): (() => void) => {
  try {
    let q = query(collection(db, "users"), orderBy("firstName"));
    
    if (roles && roles.length > 0) {
      q = query(collection(db, "users"), where("role", "in", roles), orderBy("firstName"));
    }
    
    const unsubscribe = onSnapshot(q, (querySnapshot) => {
      const users = querySnapshot.docs.map(doc => ({ 
        id: doc.id, 
        ...doc.data() 
      } as User));
      callback(users);
    }, (error) => {
      console.error("Error in users subscription:", error);
      // Call callback with empty array on error
      callback([]);
    });
    
    return unsubscribe;
  } catch (error) {
    console.error("Error setting up users subscription:", error);
    // Return a no-op unsubscribe function
    return () => {};
  }
};

// Validate username using Firebase function
export const validateUsername = async (username: string): Promise<{ isValid: boolean; message?: string }> => {
  try {
    const validateUsernameCallable = httpsCallable(functions, 'validateUsername');
    const result = await validateUsernameCallable({ username });
    
    const responseData = result.data as { isValid: boolean; message?: string };
    return responseData;
  } catch (error: any) {
    console.error("Error validating username:", error);
    return {
      isValid: false,
      message: error.message || "Failed to validate username. Please try again."
    };
  }
};

// Delete user
export const deleteUser = async (uid: string): Promise<void> => {
  try {
    await deleteDoc(doc(db, "users", uid));
  } catch (error) {
    console.error("Error deleting user:", error);
    throw error;
  }
};